/**
 * Shared Tour Helpers & Types
 * Common utilities used across all tour step configurations
 */

export interface DemoStep {
  element: string
  popover: {
    title: string
    description: string
    side?: 'left' | 'right' | 'top' | 'bottom'
    align?: 'start' | 'center' | 'end'
  }
  onHighlighted?: () => void
  onDeselected?: () => void // Fires when user clicks Next - use for navigation clicks
}

// ---------------------------------------------------------------------------
// Driver instance management
// ---------------------------------------------------------------------------
// driver.js v1.3.1 provides a `refresh()` method that recalculates the
// highlight position and repositions the popover. This is far more reliable
// than dispatching resize events, especially after async DOM changes (tab
// switches, view mode changes, etc.).
//
// We store the active driver instance here so all helper functions can call
// `refreshDriver()` after completing their async work.
// ---------------------------------------------------------------------------

let _activeDriver: { refresh: () => void } | null = null

/** Store the active driver.js instance (called from useTour when tour starts) */
export function setActiveDriver(d: { refresh: () => void } | null): void {
  _activeDriver = d
}

/**
 * Refresh the driver.js highlight position.
 * Uses the official `refresh()` API which recalculates the element's bounding
 * rect and repositions both the overlay cutout and the popover.
 */
export function refreshDriver(): void {
  _activeDriver?.refresh()
}

/**
 * Click a Radix UI tab and wait for it to become active before refreshing the highlight.
 * Replaces the duplicated setTimeout/dispatch pattern across all tour steps.
 *
 * The key challenge: when transitioning between tour steps, driver.js calculates the
 * highlight position immediately. If the page is still scrolled/positioned from a
 * previous step, the highlight lands in the wrong place. We fix this by:
 * 1. Always scrolling the tab into view (instant, no animation)
 * 2. Clicking the tab to activate it
 * 3. Calling driver.refresh() to recalculate once the tab content renders
 */
export function clickTabAndWait(selector: string): void {
  setTimeout(() => {
    const tab = document.querySelector(selector) as HTMLElement
    if (!tab) return

    // Always scroll to the tab — during step transitions the page may be
    // positioned from a previous step, so viewport checks are unreliable
    tab.scrollIntoView({ behavior: 'instant', block: 'center' })

    // Dispatch proper mouse events for Radix UI tabs
    tab.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }))
    tab.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }))
    tab.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }))

    // Poll for tab activation then refresh highlight position
    const checkActive = () => {
      if (tab.getAttribute('data-state') === 'active') {
        refreshDriver()
        setTimeout(refreshDriver, 100)
        setTimeout(refreshDriver, 300)
      } else {
        requestAnimationFrame(checkActive)
      }
    }
    setTimeout(checkActive, 50)
  }, 100)
}

/**
 * Scroll a non-tab element into view and refresh the driver.js highlight position.
 * Useful for elements that may be below the fold (cards, dialogs, etc).
 */
export function scrollAndHighlight(selector: string): void {
  setTimeout(() => {
    const el = document.querySelector(selector) as HTMLElement
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setTimeout(refreshDriver, 200)
  }, 100)
}

/**
 * Activate the objectives tab and wait for its content to render.
 * Uses the data-tour attribute to target specifically the Team Member Journey tab
 * (avoids matching other [value="objectives"] tabs on the page).
 * Calls the callback once the tab is active (or immediately if already active).
 */
function activateObjectivesTab(callback: () => void): void {
  const objTab = document.querySelector('[data-tour="tab-objectives"]') as HTMLElement
  if (!objTab) { callback(); return }

  if (objTab.getAttribute('data-state') === 'active') {
    callback()
    return
  }

  // Scroll into view first so the click targets correctly
  objTab.scrollIntoView({ behavior: 'instant', block: 'center' })

  // Click the tab using full mouse event sequence for Radix UI
  objTab.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }))
  objTab.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }))
  objTab.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }))

  // Wait for tab to become active before proceeding
  const startTime = Date.now()
  const waitForActive = () => {
    if (objTab.getAttribute('data-state') === 'active') {
      // Give React time to render the tab content
      setTimeout(() => callback(), 150)
    } else if (Date.now() - startTime < 2000) {
      setTimeout(waitForActive, 30)
    } else {
      callback() // Fallback — proceed anyway after 2s
    }
  }
  setTimeout(waitForActive, 30)
}

/**
 * Ensure the objective configuration view is open (viewMode='timeline').
 * If the config button exists (meaning we're in KPA/empty-state view),
 * click it to switch to the config view. Then poll for the target element.
 * If the target element already exists, just refreshes the highlight.
 *
 * Handles the async chain: activate objectives tab → wait for render →
 * click configure button → poll for target element → refresh driver.
 */
export function ensureObjectiveConfigOpen(targetSelector: string, callback?: (el: HTMLElement) => void): void {
  setTimeout(() => {
    // Check if target already exists (config view already open on correct tab)
    const existing = document.querySelector(targetSelector) as HTMLElement
    if (existing && existing.getBoundingClientRect().width > 0) {
      existing.scrollIntoView({ behavior: 'instant', block: 'center' })
      refreshDriver()
      setTimeout(refreshDriver, 100)
      if (callback) callback(existing)
      return
    }

    // Activate objectives tab first, then open config view
    activateObjectivesTab(() => {
      // After tab is active and content rendered, check again
      const existingAfterTab = document.querySelector(targetSelector) as HTMLElement
      if (existingAfterTab && existingAfterTab.getBoundingClientRect().width > 0) {
        existingAfterTab.scrollIntoView({ behavior: 'instant', block: 'center' })
        refreshDriver()
        setTimeout(refreshDriver, 100)
        if (callback) callback(existingAfterTab)
        return
      }

      // Config view not open — click the configure button to open it
      const configBtn = document.querySelector('[data-tour="configure-objectives"]') as HTMLElement
      if (configBtn) {
        configBtn.click()
      }

      // Poll for the target element to appear after view switch
      pollForElement(targetSelector, (el) => {
        el.scrollIntoView({ behavior: 'instant', block: 'center' })
        refreshDriver()
        setTimeout(refreshDriver, 100)
        setTimeout(refreshDriver, 300)
        if (callback) callback(el)
      })
    })
  }, 100)
}

/**
 * Ensure the objective dashboard view is showing (viewMode='kpa').
 * If the return-dashboard button exists (meaning we're in config view),
 * click it to switch back. Also ensures the objectives tab is active.
 */
export function ensureObjectiveDashboardView(): void {
  setTimeout(() => {
    activateObjectivesTab(() => {
      // If we're in config view, click return to go back to dashboard
      const returnBtn = document.querySelector('[data-tour="return-dashboard"]') as HTMLElement
      if (returnBtn) {
        returnBtn.click()
      }
      setTimeout(refreshDriver, 200)
      setTimeout(refreshDriver, 400)
    })
  }, 100)
}

/**
 * Poll until an element exists and is visible, then execute a callback.
 * Useful for elements inside lazy-loaded tab content.
 */
export function pollForElement(selector: string, callback: (el: HTMLElement) => void, maxWaitMs = 2000): void {
  const startTime = Date.now()
  const check = () => {
    const el = document.querySelector(selector) as HTMLElement
    if (el && el.getBoundingClientRect().width > 0) {
      callback(el)
    } else if (Date.now() - startTime < maxWaitMs) {
      setTimeout(check, 50)
    }
  }
  setTimeout(check, 100)
}
